import { useEffect, useState, type ReactNode } from 'react'
import { Switch, Tooltip } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { IconCopy, IconStar, IconStarFilled } from '@tabler/icons-react'
import ReactMarkdown from 'react-markdown'
import { Link } from 'react-router-dom'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import { cn } from '@/utils/cn'
import { costEmphasisClass, formatUsd } from '../cost'
import type { AssistantUsage, DisplayEntry } from '../useAssistant'
import { ArtifactsView } from './ArtifactsView'

type MessageBubbleProps = {
  entry: DisplayEntry
  onFollowUp?: (text: string) => void
  // Opens the patient picker for a turn whose `artifacts.request` asks for a patient. Receives the
  // optional `mask` search hint. Passed only for the thread's latest assistant bubble.
  onSelectPatient?: (mask?: string) => void
  isFavorite?: (text: string) => boolean
  onToggleFavorite?: (text: string) => void
  showRawResponse?: boolean
  // True when the entry belongs to a sub-agent thread the user drilled into;
  // tints this thread's debug bubbles cyan instead of amber.
  subAgentThread?: boolean
  // Path of the thread currently displayed. Combined with a sub-agent tool's
  // `threadId` input to address its sub-thread for the transcript link.
  threadPath?: string[] | null
  // Opens a thread by its full path — same effect as picking it from the
  // conversation dropdown.
  onOpenThread?: (threadPath: string[]) => void
}

const STREAMING_ELAPSED_VISIBLE_AFTER_SECONDS = 30

const HiddenCodeBlock = ({ children }: { children: string }) => (
  <pre className="max-h-72 overflow-auto font-mono text-[11px] leading-relaxed break-words whitespace-pre-wrap">
    {children}
  </pre>
)

// Debug bubbles use an amber tone in the main conversation and a cyan tone
// when the user is viewing a sub-agent thread, so it's obvious at a glance
// which thread's debug output you're looking at.
type HiddenTone = 'amber' | 'cyan'

const toneClasses: Record<HiddenTone, { box: string; caret: string }> = {
  amber: { box: 'border-amber-200 bg-amber-50 text-amber-950', caret: 'text-amber-700' },
  cyan: { box: 'border-cyan-200 bg-cyan-50 text-cyan-950', caret: 'text-cyan-700' },
}

// Sub-agent-invoking tools (`agent_*`) identify their sub-thread by a leaf
// `threadId`. Follow-up calls pass it in the input (present the moment the call
// appears); a fresh call mints it server-side and only returns it in the
// output. Prefer the input, fall back to the output once the tool finishes. The
// sub-thread's full path is the caller's path plus that leaf id.
const readThreadId = (value: unknown): string | undefined => {
  if (value && typeof value === 'object' && 'threadId' in value) {
    const id = (value as { threadId?: unknown }).threadId
    if (typeof id === 'string' && id.length > 0) return id
  }
  return undefined
}

const subThreadIdFromOutput = (output: string | undefined): string | undefined => {
  if (!output) return undefined
  try {
    return readThreadId(JSON.parse(output))
  } catch {
    return undefined
  }
}

const buildTranscriptAction = (
  name: string,
  input: unknown,
  output: string | undefined,
  threadPath: string[] | null | undefined,
  onOpenThread: ((threadPath: string[]) => void) | undefined,
): ReactNode => {
  if (!name.startsWith('agent_') || !threadPath || !onOpenThread) return undefined
  const subThreadId = readThreadId(input) ?? subThreadIdFromOutput(output)
  if (!subThreadId) return undefined
  const childPath = [...threadPath, subThreadId]
  // Rendered inline after the summary, separated by a wider gap (set on the
  // header row). A sibling of the toggle button, so clicking it doesn't
  // expand/collapse the bubble.
  return (
    <button
      type="button"
      className="shrink-0 font-medium text-[#2563eb] underline underline-offset-2 hover:text-[#1d4ed8]"
      onClick={() => onOpenThread(childPath)}
    >
      transcript
    </button>
  )
}

// One `label: value` row in the conversation-details bubble. The label column is
// fixed-width so every value lines up vertically; hovering the row reveals a copy
// button that copies just this value.
const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <div className="group flex items-start gap-2 py-0.5">
    <span className="w-24 shrink-0 text-right font-semibold">{label}:</span>
    <span className="min-w-0 font-mono break-all">{value}</span>
    <Tooltip label="Copy" withArrow>
      <button
        type="button"
        onClick={() =>
          void copyTextToClipboard(value).then(() =>
            showNotification({ message: `${label} copied`, color: 'green' }),
          )
        }
        className="mt-px shrink-0 rounded p-0.5 text-current opacity-0 transition group-hover:opacity-100 hover:text-[#2563eb]"
        aria-label={`Copy ${label}`}
      >
        <IconCopy size={13} />
      </button>
    </Tooltip>
  </div>
)

// The conversation-details debug bubble: `label: value` pairs stacked vertically
// with aligned values and per-row copy buttons (see DetailRow).
const ConversationDetailsBubble = ({
  pairs,
  tone,
}: {
  pairs: Array<{ label: string; value: string }>
  tone: HiddenTone
}) => (
  <div className="flex justify-start">
    <div
      className={cn(
        'max-w-[80%] rounded-md border px-3 py-2 text-xs shadow-sm',
        toneClasses[tone].box,
      )}
    >
      {pairs.map((pair) => (
        <DetailRow key={pair.label} label={pair.label} value={pair.value} />
      ))}
    </div>
  </div>
)

const formatTokens = (n: number): string => n.toLocaleString('en-US')

// Debug-only cost readout on a yellow bubble: every token counter plus the USD
// estimate. Used both per turn (under an assistant response) and rolled up for the
// whole conversation (under the top debug details).
const UsageBubble = ({ label, usage }: { label: string; usage: AssistantUsage }) => {
  const tokenFields: Array<[string, string]> = [
    ['Input', formatTokens(usage.inputTokens)],
    ['Output', formatTokens(usage.outputTokens)],
    ['Cache read', formatTokens(usage.cacheReadInputTokens ?? 0)],
    ['Cache write', formatTokens(usage.cacheCreationInputTokens ?? 0)],
  ]
  const cost = usage.costUsd
  const costClass = costEmphasisClass(cost)
  return (
    <div className="flex justify-start">
      <div className="w-fit max-w-full overflow-x-auto rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-xs text-yellow-900 shadow-sm">
        <div className="mb-1 font-medium">{label}</div>
        <div className="flex flex-nowrap items-center gap-x-3 whitespace-nowrap">
          {tokenFields.map(([k, v]) => (
            <span key={k}>
              <span className="opacity-70">{k}</span> {v}
            </span>
          ))}
          <span>
            <span className="opacity-70">Cost</span>{' '}
            <span className={costClass}>{cost !== undefined ? formatUsd(cost) : '—'}</span>
          </span>
        </div>
      </div>
    </div>
  )
}

const CollapsibleHiddenBubble = ({
  summary,
  children,
  className,
  tone = 'amber',
  action,
}: {
  summary: string
  children: ReactNode
  className?: string
  tone?: HiddenTone
  // Optional control rendered inline right after the summary (e.g. a transcript
  // link). Kept a sibling of the toggle button, not nested, so clicking it
  // doesn't expand/collapse the bubble.
  action?: ReactNode
}) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="flex justify-start">
      <div
        className={cn(
          'rounded-md border px-3 py-2 text-xs shadow-sm',
          toneClasses[tone].box,
          className ?? 'max-w-[80%]',
        )}
      >
        <div className="flex items-center gap-4">
          <button
            type="button"
            className="flex min-w-0 items-center gap-2 text-left font-medium"
            aria-expanded={expanded}
            onClick={() => setExpanded((value) => !value)}
          >
            <span className={cn('w-3 shrink-0', toneClasses[tone].caret)} aria-hidden="true">
              {expanded ? '▾' : '▸'}
            </span>
            <span className="min-w-0 truncate">{summary}</span>
          </button>
          {action}
        </div>
        {expanded && <div className="mt-2">{children}</div>}
      </div>
    </div>
  )
}

const CollapsibleHiddenBlock = ({
  summary,
  children,
  className,
  tone = 'amber',
}: {
  summary: string
  children: ReactNode
  className?: string
  tone?: HiddenTone
}) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <div
      className={cn(
        'rounded-md border px-3 py-2 text-xs shadow-sm',
        toneClasses[tone].box,
        className,
      )}
    >
      <button
        type="button"
        className="flex w-full min-w-0 items-center gap-2 text-left font-medium"
        aria-expanded={expanded}
        onClick={() => setExpanded((value) => !value)}
      >
        <span className={cn('w-3 shrink-0', toneClasses[tone].caret)} aria-hidden="true">
          {expanded ? '▾' : '▸'}
        </span>
        <span className="min-w-0 truncate">{summary}</span>
      </button>
      {expanded && <div className="mt-2">{children}</div>}
    </div>
  )
}

const ToolCallResultBubble = ({
  entry,
  tone,
  threadPath,
  onOpenThread,
}: {
  entry: Extract<DisplayEntry, { kind: 'tool_call_result' }>
  tone: HiddenTone
  threadPath?: string[] | null
  onOpenThread?: (threadPath: string[]) => void
}) => {
  const [renderArtifacts, setRenderArtifacts] = useState(false)
  const a = entry.artifacts
  const hasArtifacts = Boolean(
    a &&
    ((a.charts?.length ?? 0) > 0 ||
      (a.tables?.length ?? 0) > 0 ||
      (a.kpis?.length ?? 0) > 0 ||
      (a.answer && a.answer.trim().length > 0)),
  )

  return (
    <CollapsibleHiddenBubble
      summary={`${entry.name} - ${entry.toolUseId}`}
      className="w-full max-w-[95%]"
      tone={tone}
      action={buildTranscriptAction(
        entry.name,
        entry.input,
        entry.output,
        threadPath,
        onOpenThread,
      )}
    >
      <div className="grid gap-3 md:grid-cols-2">
        <div className="min-w-0">
          <div className="mb-1 font-medium">Call</div>
          <HiddenCodeBlock>{formatUnknown(entry.input)}</HiddenCodeBlock>
        </div>
        <div className="min-w-0">
          <div className="mb-1 font-medium">Result</div>
          <HiddenCodeBlock>
            {entry.output !== undefined ? formatJsonString(entry.output) : 'Pending result'}
          </HiddenCodeBlock>
        </div>
      </div>
      {hasArtifacts && (
        <div className="mt-3 border-t border-black/10 pt-2">
          <Switch
            size="xs"
            checked={renderArtifacts}
            onChange={(event) => setRenderArtifacts(event.currentTarget.checked)}
            label="Render artifacts"
          />
          {renderArtifacts && (
            <div className="mt-2 flex flex-col gap-2">
              <ArtifactsView artifacts={entry.artifacts} />
            </div>
          )}
        </div>
      )}
    </CollapsibleHiddenBubble>
  )
}

export const TypingDots = () => (
  <span className="inline-flex items-center gap-1 py-1">
    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-400 [animation-delay:-0.3s]" />
    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-400 [animation-delay:-0.15s]" />
    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-400" />
  </span>
)

type TypingBubbleProps = {
  thinkingText?: string
  activityLabel?: string
  phaseLabel?: string
}

export const TypingBubble = ({
  thinkingText,
  activityLabel,
  phaseLabel,
}: TypingBubbleProps = {}) => {
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const showElapsed = elapsedSeconds >= STREAMING_ELAPSED_VISIBLE_AFTER_SECONDS

  useEffect(() => {
    const startedAt = Date.now()
    const updateElapsed = () => setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000))
    const intervalId = window.setInterval(updateElapsed, 1000)

    updateElapsed()
    return () => window.clearInterval(intervalId)
  }, [])

  const headerLabel = activityLabel ?? phaseLabel
  const hasContent = Boolean(thinkingText || headerLabel)

  return (
    <div className="flex justify-start">
      <div className="flex max-w-[80%] items-start gap-2">
        <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#2563eb] to-[#7c3aed] text-xs font-bold text-white">
          ✨
        </div>
        <div className="flex flex-col gap-1 rounded-2xl rounded-bl-sm border border-[#e2e8f0] bg-white px-3.5 py-2 shadow-sm">
          <div className="flex items-center gap-2">
            <TypingDots />
            {headerLabel && (
              <span className="text-xs font-medium whitespace-nowrap text-slate-700">
                {headerLabel}
              </span>
            )}
            {showElapsed && (
              <span className="text-xs font-medium whitespace-nowrap text-slate-500">
                {elapsedSeconds}s elapsed
              </span>
            )}
          </div>
          {thinkingText && (
            <div className="prose prose-sm prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-headings:my-1 m-0 max-h-40 overflow-auto text-xs leading-snug text-slate-500 italic">
              <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                {thinkingText}
              </ReactMarkdown>
            </div>
          )}
          {!hasContent && !showElapsed && null}
        </div>
      </div>
    </div>
  )
}

export const MessageBubble = ({
  entry,
  onFollowUp,
  onSelectPatient,
  isFavorite,
  onToggleFavorite,
  showRawResponse = false,
  subAgentThread = false,
  threadPath,
  onOpenThread,
}: MessageBubbleProps) => {
  const tone: HiddenTone = subAgentThread ? 'cyan' : 'amber'

  if (entry.kind === 'hidden') {
    const pairs: Array<{ label: string; value: string }> = [
      { label: 'Provider', value: entry.provider ?? 'unknown' },
      { label: 'Model', value: entry.model ?? 'unknown' },
      ...(entry.maxTokens != null ? [{ label: 'Max tokens', value: String(entry.maxTokens) }] : []),
      ...(entry.forcedCurrentDate
        ? [{ label: 'Forced date', value: entry.forcedCurrentDate }]
        : []),
      ...(entry.timezone ? [{ label: 'Timezone', value: entry.timezone }] : []),
      ...(entry.conversationKey ? [{ label: 'Key', value: entry.conversationKey }] : []),
    ]

    return (
      <>
        <ConversationDetailsBubble pairs={pairs} tone={tone} />
        {entry.usage && <UsageBubble label="Conversation cost" usage={entry.usage} />}
      </>
    )
  }

  if (entry.kind === 'system') {
    return (
      <CollapsibleHiddenBubble summary="System" tone={tone}>
        <HiddenCodeBlock>{entry.text}</HiddenCodeBlock>
      </CollapsibleHiddenBubble>
    )
  }

  if (entry.kind === 'tool_call_result') {
    return (
      <ToolCallResultBubble
        entry={entry}
        tone={tone}
        threadPath={threadPath}
        onOpenThread={onOpenThread}
      />
    )
  }

  if (entry.kind === 'thinking') {
    const text = entry.text.trim()
    return (
      <div className="flex justify-start">
        <div
          className={cn(
            'prose prose-sm prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-headings:my-1 max-w-[80%] rounded-md border px-3 py-1.5 text-xs shadow-sm',
            toneClasses[tone].box,
          )}
        >
          <span className="font-medium">thinking:</span>{' '}
          {text ? (
            <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{text}</ReactMarkdown>
          ) : (
            '(empty)'
          )}
        </div>
      </div>
    )
  }

  if (entry.kind === 'retry') {
    const errorText = entry.assistant.error ?? 'The response could not be validated.'
    const rejectedResponse = entry.assistant.response?.trim()
    return (
      <div className="flex justify-start">
        <div className="flex max-w-[80%] flex-col gap-1.5">
          <div className="prose prose-sm prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-headings:my-1 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs text-red-950 shadow-sm">
            <span className="font-medium">retry:</span>{' '}
            <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{errorText}</ReactMarkdown>
          </div>
          {rejectedResponse && (
            <CollapsibleHiddenBlock
              summary="Rejected response"
              className="border-red-200 bg-red-50 text-red-950"
            >
              <HiddenCodeBlock>{rejectedResponse}</HiddenCodeBlock>
            </CollapsibleHiddenBlock>
          )}
        </div>
      </div>
    )
  }

  if (entry.kind === 'user') {
    const favorite = isFavorite?.(entry.text) ?? false
    const handleCopy = async () => {
      try {
        await copyTextToClipboard(entry.text)
        showNotification({ color: 'green', message: 'Copied' })
      } catch {
        showNotification({ color: 'red', message: 'Failed to copy message' })
      }
    }

    return (
      <div className="flex justify-end">
        <div className="flex max-w-[80%] items-end gap-2">
          {onToggleFavorite && (
            <Tooltip label={favorite ? 'Remove favorite' : 'Add favorite'} withArrow>
              <button
                type="button"
                onClick={() => onToggleFavorite(entry.text)}
                className={cn(
                  'mb-1 rounded p-1 text-slate-400 transition hover:text-amber-500',
                  favorite && 'text-amber-500',
                )}
                aria-label={favorite ? 'Remove favorite' : 'Add favorite'}
              >
                {favorite ? <IconStarFilled size={14} /> : <IconStar size={14} />}
              </button>
            </Tooltip>
          )}
          <Tooltip label="Copy message" withArrow>
            <button
              type="button"
              onClick={() => void handleCopy()}
              className="mb-1 rounded p-1 text-slate-400 transition hover:text-[#2563eb]"
              aria-label="Copy message"
            >
              <IconCopy size={14} />
            </button>
          </Tooltip>
          <div className="rounded-2xl rounded-br-sm bg-[#2563eb] px-3.5 py-2 text-sm text-white shadow-sm">
            <div className="prose prose-sm prose-p:my-0 prose-p:text-white max-w-none leading-relaxed break-words whitespace-pre-wrap text-white">
              <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkBreaks]}
                components={{
                  a: ({ href, children }) =>
                    href?.startsWith('/patients/') ? (
                      <Link
                        to={href}
                        className="font-medium text-white underline underline-offset-2"
                      >
                        {children}
                      </Link>
                    ) : (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-white underline underline-offset-2"
                      >
                        {children}
                      </a>
                    ),
                }}
              >
                {entry.text}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const rawResponse = showRawResponse ? entry.response : undefined
  const rawResponseDisplay = rawResponse ? formatRawModelResponse(rawResponse) : undefined
  const validationError = showRawResponse ? entry.error : undefined
  // Structured artifacts widen the bubble; they only accompany a completed turn.
  const richArtifacts = entry.status === 'complete' ? entry.artifacts : undefined
  const hasRichContent = Boolean(
    richArtifacts?.kpis?.length || richArtifacts?.charts?.length || richArtifacts?.tables?.length,
  )
  // The "Select patient" action for a turn that requested one, when this is the
  // thread's latest bubble (MessageList only passes onSelectPatient then). It's a
  // full-width row aligned to the chat's right edge, below the answer.
  const showSelectPatient =
    entry.status === 'complete' &&
    entry.artifacts?.request?.entity === 'patient' &&
    Boolean(onSelectPatient)

  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-start">
        <div
          className={cn(
            'flex items-start gap-2',
            hasRichContent ? 'w-full max-w-[95%]' : 'max-w-[80%]',
          )}
        >
          <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#2563eb] to-[#7c3aed] text-xs font-bold text-white">
            ✨
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            {rawResponseDisplay && (
              <CollapsibleHiddenBlock
                summary="Raw model response"
                tone={tone}
                className={
                  rawResponseDisplay.invalidJson
                    ? 'border-red-200 bg-red-50 text-red-950'
                    : undefined
                }
              >
                <HiddenCodeBlock>{rawResponseDisplay.text}</HiddenCodeBlock>
              </CollapsibleHiddenBlock>
            )}
            {validationError && (
              <div className="max-w-[80%] rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-950 shadow-sm">
                <div className="mb-1 font-medium">Validation error</div>
                <HiddenCodeBlock>{validationError}</HiddenCodeBlock>
              </div>
            )}
            <ArtifactsView
              artifacts={entry.artifacts}
              status={entry.status}
              onFollowUp={onFollowUp}
            />
            {showRawResponse && entry.usage && (
              <UsageBubble label="Turn cost" usage={entry.usage} />
            )}
          </div>
        </div>
      </div>
      {showSelectPatient && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => onSelectPatient?.(entry.artifacts?.request?.mask)}
            // Same accent as the Home page "Compose" button.
            className="inline-flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:brightness-95"
            style={{ backgroundColor: 'rgb(229, 83, 61)' }}
          >
            Select patient
          </button>
        </div>
      )}
    </div>
  )
}

async function copyTextToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }

  const textArea = document.createElement('textarea')
  textArea.value = text
  textArea.setAttribute('readonly', '')
  textArea.style.position = 'fixed'
  textArea.style.opacity = '0'
  document.body.appendChild(textArea)
  textArea.select()

  const copied = document.execCommand('copy')
  document.body.removeChild(textArea)

  if (!copied) {
    throw new Error('Copy command failed')
  }
}

function formatUnknown(value: unknown): string {
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value, null, 2) ?? 'undefined'
  } catch {
    return String(value)
  }
}

function formatJsonString(value: string): string {
  try {
    return JSON.stringify(JSON.parse(value), null, 2)
  } catch {
    return value
  }
}

function formatRawModelResponse(value: string): { text: string; invalidJson: boolean } {
  try {
    return { text: JSON.stringify(JSON.parse(value), null, 2), invalidJson: false }
  } catch {
    return { text: value, invalidJson: true }
  }
}
